import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { Portal } from "components/Portal";
import { Horizontal, Vertical } from "gls/lib";
import React, { MutableRefObject, useRef, useState } from "react";
import { ScrollButton } from "components/ScrollContainer/ScrollButton";
import LoadingScreen from "components/Loading/LoadingScreen";
import { wrapperStyle } from "./components/tableStyle";
import { EmptyListMessage } from "./components/EmptyListMessage";
import { MobileSort } from "./components/MobileSort";
import { colours } from "styling/colours";
import { icons } from 'assets/icons/index'
import { style } from "typestyle";

export type MobileSortOption = {
  title: string;
  sortField: string;
  AscTitle?: string;
  DescTitle?: string;
};

type MobileDataTableProps<T> = {
  data: T[];
  id?: string;
  renderLoadMore?: React.ReactNode;
  height?: string;
  loading?: boolean;
  showTopButton?: boolean;
  topButtonClick?: () => void;
  onSort?: (field: string, direction: string) => void;
  onRowClick?: (rowValues: T) => void
  emptyListMsg?: string

  RowElement: (row: T) => React.ReactElement;
  SortOptions?: MobileSortOption[];
  defaultSortField: string;
  defaultSortDirection: "asc" | "desc";
};

export const MobileDataTable = <T extends Record<string, any>>(props: MobileDataTableProps<T>) => {
  const [showOnScroll, setShowOnScroll] = useState(false);
  const boundingElementRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const {
    showTopButton = true,
    RowElement,
    id = 'mobile-data-table',
    loading,
    onSort,
    SortOptions,
    defaultSortField,
    defaultSortDirection,
    renderLoadMore,
    data,
    emptyListMsg = "No items to display"
  } = props;

  useScrollPosition(({ prevPos, currPos }) => {
    const isShow = currPos.y > 10;
    if (isShow !== showOnScroll) setShowOnScroll(isShow);
  },
    [showOnScroll],
    elementRef as MutableRefObject<HTMLDivElement>,
    false,
    300,
    boundingElementRef as MutableRefObject<HTMLDivElement>
  );

  const handleScroll = () => {
    props.topButtonClick && props.topButtonClick()
    boundingElementRef.current?.scrollTo({
      behavior: "smooth",
      top: 0,
    });
  };

  const [showSort, setShowSort] = useState(false);
  const [sortValue, setSortValue] = useState<{
    field: string;
    direction: string;
  }>({ field: defaultSortField, direction: defaultSortDirection });

  const handleSort = (sortField: string, direction: string) => {
    setSortValue({
      field: sortField,
      direction: direction,
    });
    return onSort && onSort(sortField, direction);
  };
  if (showSort) {
    return (
      <MobileSort
        SortOptions={SortOptions}
        handleSort={handleSort}
        defaultSortField={sortValue.field}
        defaultSortDirection={sortValue.direction}
        onClose={() => setShowSort(false)}
      />
    );
  }

  if (data.length === 0) {
    if (loading)
      return (
        <Vertical className={wrapperStyle} sizing="stretch">
          <LoadingScreen />
        </Vertical>
      )
    else
      return (
        <Vertical className={wrapperStyle} sizing="stretch">
          <EmptyListMessage title={emptyListMsg} />
        </Vertical>
      )
  }

  return (
    <>
      {SortOptions && (
        <Horizontal id='mobile-sort-button'
          spacing={5} className={sortStyle} horizontalAlign="right" onClick={() => setShowSort(true)}>
          Sort
          <icons.SortIcon aria-label='Sort' width={20} fill={colours.secondary} />
        </Horizontal>
      )}
      <Vertical className={wrapperStyle} sizing="stretch" ref={boundingElementRef}>
        <div ref={elementRef} id={id} className={dataTableStyle}>
          {props.data.map((row, index) =>
            <div className="row" key={`table-row-${index}`} id={`table-row-${index}`}>{RowElement(row)}</div>)}
          {renderLoadMore && renderLoadMore}
        </div >
      </Vertical >
      <Portal>
        {showTopButton && showOnScroll && <ScrollButton right="20px" onClick={handleScroll} />}
      </Portal>
    </>
  );
};

const sortStyle = style({
  color: colours.secondary,
  cursor: "pointer",
  marginBottom: '10px'
})

const dataTableStyle = style({
  $nest: {
    ".row": {
      backgroundColor: colours.white,
      borderRadius: '6px',
      border: `1px solid ${colours.border}`,
      padding: '20px',
      marginBottom: '10px'
    }
  }
})
